import React from "react";
import { Box, Tooltip } from "@mui/material";
import { gql, useQuery } from "@apollo/client";
import { dietaryTagIcons as DIETARY_TAG_ICONS_QUERY } from "../../../apollo/server";
import { resizedImage } from "../../../utils/resizedImage";

const GET_DIETARY_TAG_ICONS = gql`${DIETARY_TAG_ICONS_QUERY}`;

// Hand-crafted, distinct flat SVG icons per dietary tag, each shown in its own
// colored circle. Keys match the admin's dietaryTags values exactly.
// Each icon is a 24x24 viewBox path set; `c` is the icon stroke/fill color and
// `bg` the circle background.

const S = 18; // icon size inside the circle

// Small helpers for the recurring "slash / X" overlay (free-of) badges.
const Slash = ({ color }) => (
  <line x1="4" y1="20" x2="20" y2="4" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
);

const ICONS = {
  Spicy: {
    c: "#dc2626", bg: "#fee2e2",
    svg: (c) => (
      <g fill={c}>
        <path d="M7 14c0 3 2 5 5 5s5-2 5-5c0-3-2-6-5-9-1 2-1 3 0 4-3 0-5 2-5 5z" />
        <path d="M12 5c1-1 3-2 4-1" stroke={c} strokeWidth="1.6" fill="none" strokeLinecap="round" />
      </g>
    ),
  },
  Vegetarian: {
    c: "#16a34a", bg: "#dcfce7",
    svg: (c) => (
      <path fill={c} d="M5 19c0-7 6-13 14-13 0 8-6 14-13 14 0-4 2-7 6-9-3 1-5 3-7 8z" />
    ),
  },
  Vegan: {
    c: "#15803d", bg: "#dcfce7",
    svg: (c) => (
      <g fill={c}>
        <path d="M12 21v-7" stroke={c} strokeWidth="2" strokeLinecap="round" />
        <path d="M12 14c-4 0-6-3-6-6 4 0 6 2 6 6z" />
        <path d="M12 12c0-4 2-6 6-6 0 3-2 6-6 6z" />
      </g>
    ),
  },
  "Gluten-Free": {
    c: "#b45309", bg: "#fef3c7",
    svg: (c) => (
      <g>
        <g stroke={c} strokeWidth="1.6" strokeLinecap="round" fill="none">
          <path d="M12 20V8" />
          <path d="M12 9c-2-1-3-3-3-5 2 0 3 1 3 3M12 9c2-1 3-3 3-5-2 0-3 1-3 3" />
          <path d="M12 14c-2-1-3-3-3-5 2 0 3 1 3 3M12 14c2-1 3-3 3-5-2 0-3 1-3 3" />
        </g>
        <Slash color="#dc2626" />
      </g>
    ),
  },
  "Dairy-Free": {
    c: "#0369a1", bg: "#e0f2fe",
    svg: (c) => (
      <g>
        <path fill={c} d="M9 4h6v3l1 3v9H8v-9l1-3z" opacity="0.85" />
        <Slash color="#dc2626" />
      </g>
    ),
  },
  Kosher: {
    c: "#1d4ed8", bg: "#dbeafe",
    svg: (c) => (
      <g stroke={c} strokeWidth="1.6" fill="none" strokeLinejoin="round">
        <path d="M12 4l7 12H5z" />
        <path d="M12 20L5 8h14z" />
      </g>
    ),
  },
  Halal: {
    c: "#0d9488", bg: "#ccfbf1",
    svg: (c) => (
      <g fill={c}>
        <path d="M16 5a8 8 0 1 0 0 14 6.5 6.5 0 0 1 0-14z" />
        <path d="M18 9l.7 1.6L20.5 11l-1.5 1 .4 1.8L18 12.9l-1.4.9.4-1.8-1.5-1 1.8-.4z" />
      </g>
    ),
  },
  Organic: {
    c: "#15803d", bg: "#dcfce7",
    svg: (c) => (
      <path fill={c} d="M12 20s-7-4-7-9a3.5 3.5 0 0 1 7-1 3.5 3.5 0 0 1 7 1c0 5-7 9-7 9z" />
    ),
  },
  Keto: {
    c: "#6d28d9", bg: "#ede9fe",
    svg: (c) => (
      <g fill={c}>
        <path d="M13 3L6 13h4l-1 8 7-11h-4z" />
      </g>
    ),
  },
  Paleo: {
    c: "#92400e", bg: "#fde68a",
    svg: (c) => (
      <path fill={c} d="M7 7a2.5 2.5 0 1 1 3 3l4 4a2.5 2.5 0 1 1-1 1l-4-4a2.5 2.5 0 1 1-2-4z" />
    ),
  },
  "Sugar-Free": {
    c: "#c2410c", bg: "#ffedd5",
    svg: (c) => (
      <g>
        <rect x="7" y="7" width="10" height="10" rx="2" fill={c} opacity="0.85" />
        <Slash color="#dc2626" />
      </g>
    ),
  },
  "Nut-Free": {
    c: "#7c2d12", bg: "#fef3c7",
    svg: (c) => (
      <g>
        <path fill={c} d="M9 6c3-1 6 1 6 4 0 4-2 8-5 8s-4-3-4-6c0-3 0-5 3-6z" opacity="0.85" />
        <Slash color="#dc2626" />
      </g>
    ),
  },
};

const CircleBadge = ({ tag }) => {
  const def = ICONS[tag];
  if (!def) return null;
  return (
    <Tooltip title={tag} arrow>
      <Box
        sx={{
          width: 26, height: 26, borderRadius: "50%", bgcolor: def.bg,
          display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}
      >
        <svg width={S} height={S} viewBox="0 0 24 24">{def.svg(def.c)}</svg>
      </Box>
    </Tooltip>
  );
};

// "New" is a starburst pill, like the reference design.
const NewPill = () => (
  <Box
    component="span"
    sx={{
      display: "inline-flex", alignItems: "center", gap: 0.25,
      bgcolor: "#fde68a", color: "#92400e", borderRadius: "999px",
      px: 0.8, height: 18, fontSize: "0.6rem", fontWeight: 800,
      letterSpacing: "0.04em", textTransform: "uppercase",
    }}
  >
    <svg width="10" height="10" viewBox="0 0 24 24" fill="#f59e0b">
      <path d="M12 2l2.4 6.5L21 11l-6.6 2.5L12 20l-2.4-6.5L3 11l6.6-2.5z" />
    </svg>
    New
  </Box>
);

// A generated image badge (preferred), falling back to the inline SVG.
const ImageBadge = ({ tag, url }) => (
  <Tooltip title={tag} arrow>
    <Box
      component="img"
      src={resizedImage(url, 128)}
      alt={tag}
      sx={{ width: 26, height: 26, borderRadius: "50%", flexShrink: 0, objectFit: "cover" }}
    />
  </Tooltip>
);

const DietaryBadges = ({ tags }) => {
  // Cached across the page — fetched once.
  const { data } = useQuery(GET_DIETARY_TAG_ICONS, { fetchPolicy: "cache-first" });
  const iconMap = React.useMemo(() => {
    const m = {};
    (data?.dietaryTagIcons || []).forEach((d) => { if (d.iconUrl) m[d.tag] = d.iconUrl; });
    return m;
  }, [data]);

  if (!tags || tags.length === 0) return null;
  // Render any tag that has either a generated image or an inline SVG fallback.
  const renderable = tags.filter((tg) => iconMap[tg] || ICONS[tg] || tg === "New");

  return (
    // Raised above the menu card's stretched link (MenuItemCard's title
    // button covers the whole card); under it, hovering never reached the
    // badges and their tooltips never opened.
    <Box sx={{ position: "relative", zIndex: 1, display: "inline-flex", alignItems: "center", gap: 0.5, ml: 0.75, verticalAlign: "middle", flexWrap: "wrap" }}>
      {renderable.map((tag) => {
        if (iconMap[tag]) return <ImageBadge key={tag} tag={tag} url={iconMap[tag]} />;
        if (tag === "New") return <NewPill key={tag} />;
        return <CircleBadge key={tag} tag={tag} />;
      })}
    </Box>
  );
};

export default DietaryBadges;
